import React, { useCallback, useMemo, useState } from "react";

import { ChartProps } from "react-chartjs-2";
import dayjs from "dayjs";
import {
  evaluateCategoriesJa,
  evaluateCategoryKeys,
  IEvaluateCategoryKeys,
} from "../../../const";
import useTimelineData from "../../../repository/useTimelineData";
import useBID from "../../../utils/useBid";
import { divisionByArray } from "utils/array";

const categoriesJP = ["総合評価", ...evaluateCategoriesJa] as const;
const categoryKeys = ["star", ...evaluateCategoryKeys] as const;

const useTimelinePage = () => {
  const bId = useBID();

  // カテゴリ
  const [activeCategoryIdx, setActiveCategoryIdx] = useState(0);
  const onCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setActiveCategoryIdx(parseInt(e.target.value));
  };

  // 間隔
  type IInterval = "day" | "week" | "month" | "year";
  const [interval, setInterval] = useState<IInterval>("week");
  const onIntervalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setInterval(e.target.value as IInterval);
  };

  const amount =
    interval === "day"
      ? 30
      : interval === "week"
      ? 8
      : interval === "month"
      ? 12
      : 5;
  const startAt = useMemo(
    () => dayjs().subtract(amount, interval).toDate(),
    [amount, interval]
  );

  const {
    data: timelineData,
    isLoading,
    isError,
  } = useTimelineData({
    bId,
    amount,
    interval,
    startAt,
  });

  const isActiveCategoryStar = activeCategoryIdx === 0;
  const startAts = timelineData?.startAts;
  const numReviews = timelineData?.counts;

  const posCountList = useMemo(
    () =>
      timelineData?.[categoryKeys[activeCategoryIdx] as IEvaluateCategoryKeys]
        .pos || [],
    [activeCategoryIdx, timelineData]
  );

  const negCountList = useMemo(
    () =>
      timelineData?.[categoryKeys[activeCategoryIdx] as IEvaluateCategoryKeys]
        .neg || [],
    [activeCategoryIdx, timelineData]
  );

  const countList = useMemo(
    () => timelineData?.counts || [],
    [timelineData?.counts]
  );

  const buildDataset = useCallback(() => {
    if (!timelineData || isLoading)
      throw new Error("ローディング中にデータセットが呼ばれています");

    if (isActiveCategoryStar)
      return [
        {
          data: timelineData.star,
          pointRadius: numReviews,
          fill: false,
          borderColor: "rgb(75, 192, 192)",
          tension: 0.2,
        },
      ];

    return [
      {
        label: "positive",
        data: divisionByArray(posCountList, countList).map((a) => a * 100),
        pointRadius: posCountList,
        fill: {
          target: "origin",
          above: "rgba(72, 199, 142, 0.2)", //#48c78e
          below: "rgba(72, 199, 142, 0.2)", //#48c78e
        },
        borderColor: "#48c78e",
        tension: 0.2,
      },
      {
        label: "negative",
        data: divisionByArray(negCountList, countList).map((a) => a * -100),
        pointRadius: negCountList,
        fill: {
          target: "origin",
          below: "rgba(241, 70, 104, 0.2)", // #f14668
          above: "rgba(241, 70, 104, 0.2)", // #f14668
        },
        borderColor: "#f14668",
        tension: 0.2,
      },
    ];
  }, [
    countList,
    isActiveCategoryStar,
    isLoading,
    negCountList,
    numReviews,
    posCountList,
    timelineData,
  ]);

  const chartTimeLineProps: ChartProps | null =
    timelineData && !isLoading
      ? {
          type: "line",
          data: {
            labels: startAts,
            datasets: buildDataset(),
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                max: isActiveCategoryStar ? 5 : 100,
                min: isActiveCategoryStar ? 0 : -100,
                title: {
                  display: true,
                  text: isActiveCategoryStar ? "星評価の大きさ" : "言及率(%)",
                },
                ticks: {
                  callback: (value) => (value < 0 ? -value : value),
                },
              },
            },
            plugins: {
              title: {
                display: true,
                text: categoriesJP[activeCategoryIdx],
              },
              legend: {
                display: false,
              },
              tooltip: {
                displayColors: false,
                callbacks: {
                  beforeLabel: () => "",
                  label: (ctx) => {
                    if (isActiveCategoryStar) {
                      return [
                        `アンケート回答数: ${
                          timelineData.counts[ctx.dataIndex]
                        }件`,
                      ];
                    }

                    const targetDataset =
                      ctx.datasetIndex === 0 ? posCountList : negCountList;
                    return [
                      categoriesJP[activeCategoryIdx],
                      `言及数: ${targetDataset[ctx.dataIndex]}件`,
                    ];
                  },
                },
              },
            },
          },
        }
      : null;

  return {
    isLoading,
    isError,
    chartTimeLineProps,
    categoriesJP,
    activeCategoryIdx,
    onCategoryChange,
    interval,
    onIntervalChange,
  };
};

export default useTimelinePage;
